<template>
  <div>
    <p v-if='!loaded'>Please search for a domain for which to manage Smarthosts.</p>
    <div class='field is-grouped'>
      <div class='control is-expanded'>
        <input
          class='input'
          type='text'
          placeholder='Domain'
          ref='domainSearch'
          v-model='domain'
          @keyup.enter='onSearch'
        />
      </div>

      <div class='control'>
        <a class='button' @click='onSearch'>
          Search
        </a>
      </div>
    </div>

    <hr>

    <div :class='$style.buttonContainer' v-if='loaded && isSmarthostAdmin'>
      <button class='button' @click='onClickAddSmarthost'>
        Add Smarthost
      </button>
    </div>

    <SmarthostList
      v-if='smarthosts.length'
      :smarthosts='smarthosts'
      :canEdit='isSmarthostAdmin'
      @rowClicked='onClickSmarthost'
    />

    <SmarthostEditor
      v-if='isEditorOpen'
      :isOpen='isEditorOpen'
      :selected='selected.smarthost'
      :index='selected.index'
      :savingErrors='savingErrors'
      @clearErrors='onClearEditorErrors'
      @success='onSuccessfulTest'
      @close='onCancelAddSmarthost'
      @delete='onDeleteSmarthost'
    />
  </div>
</template>

<script>
import useUserHasPermission from '@/composables/useUserHasPermission';
import { getSmarthosts, setSmarthosts } from '@/request';
import SmarthostList from '@/components/SmarthostList.vue';
import SmarthostEditor from '@/components/SmarthostEditor.vue';

const SMARTHOST_ADMIN_PERMISSION = 'smarthostAdmin';

const DEFAULT_SMARTHOST = {
  host: '',
  port: 25,
  priority: 0,
  requireTLS: false,
  username: '',
  password: ''
};

const getDefaultSmarthost = () => JSON.parse(JSON.stringify(DEFAULT_SMARTHOST));

export default {
  name: 'SmarthostsView',

  components: {
    SmarthostList,
    SmarthostEditor
  },

  setup() {
    const { hasPermission } = useUserHasPermission(SMARTHOST_ADMIN_PERMISSION);
    return {
      isSmarthostAdmin: hasPermission
    };
  },

  data() {
    const { domain } = this.$route.query;
    return {
      domain: domain || '',
      loaded: false,
      smarthosts: [],
      isEditorOpen: false,
      selected: {
        index: -1,
        smarthost: getDefaultSmarthost()
      },
      errors: [],
      savingErrors: []
    }
  },

  mounted() {
    if (this.domain) {
      this.fetchSmarthosts();
    } else {
      this.$nextTick(() => {
        this.$refs.domainSearch.focus();
      });
    }
  },

  methods: {
    async fetchSmarthosts() {
      try {
        this.smarthosts = await getSmarthosts(this.domain);
        this.smarthosts.sort(this.sortByPriority);
        this.error = '';
        this.loaded = true;
      }
      catch (error) {
        this.error = error.message;
        this.smarthosts = [];
        this.loaded = false;
      }
    },

    onSearch() {
      if (!this.domain.trim().length) {
        this.error = 'Please enter a domain.';
        return;
      }

      this.$router.push({
        path: '/smarthosts',
        query: { domain: this.domain }
      });

      this.fetchSmarthosts();
    },

    onClickSmarthost(smarthost, index) {
      if (!this.isSmarthostAdmin) return;

      this.selected = {
        smarthost: {
          ...smarthost
        },
        index
      };

      this.isEditorOpen = true;
    },

    onClickAddSmarthost() {
      this.isEditorOpen = true;
    },

    onSuccessfulTest(smarthost) {
      const { index } = this.selected;
      this.savingErrors = [];

      const newSmarthosts = [ ...this.smarthosts ];

      if (index > -1) {
        newSmarthosts[index] = smarthost;
      } else {
        newSmarthosts.push(smarthost);
      }

      this.saveSmarthosts(newSmarthosts)
        .then(() => {
          this.smarthosts = newSmarthosts;
          this.smarthosts.sort(this.sortByPriority);
          this.isEditorOpen = false;

          this.selected = {
            index: -1,
            smarthost: getDefaultSmarthost()
          };
        })
        .catch(err => {
          this.savingErrors = [err.message];
        });
    },

    saveSmarthosts(smarthosts) {
      return setSmarthosts(this.domain, smarthosts);
    },

    onCancelAddSmarthost() {
      this.isEditorOpen = false;
      this.savingErrors = [];
      this.selected = {
        index: -1,
        smarthost: getDefaultSmarthost()
      };
    },

    onDeleteSmarthost () {
      this.isEditorOpen = false;
      this.smarthosts.splice(this.selected.index, 1);
      this.saveSmarthosts(this.smarthosts);
      this.selected = {
        index: -1,
        smarthost: getDefaultSmarthost()
      };
    },

    onClearEditorErrors() {
      this.savingErrors = [];
    },

    sortByPriority(a, b) {
      return a.priority - b.priority;
    }
  }
}
</script>

<style lang="scss" module>
.buttonContainer {
  text-align: center;

  button {
    margin: 0 10px;
  }
}

.tableContainer {
  text-align: center;
}
</style>
